import { inject } from '@angular/core';
import { HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from '@mogenius/user/services/auth/auth.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, EMPTY, Observable, Subject, from, throwError } from 'rxjs';
import { catchError, filter, finalize, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';

// Gate: open when no refresh is in flight. While closed, every new request waits
// until the refresh finishes — never goes out with a stale access token.
const gateOpen$ = new BehaviorSubject<boolean>(true);

// Signals all waiting requests to abort (used when refresh fails).
const abort$ = new Subject<void>();

// Shared in-flight refresh observable. The first 401 (or proactive expiry) starts it;
// every subsequent caller subscribes to the same observable and gets the same result.
let refreshInFlight: Observable<unknown> | null = null;

const allowWithoutToken = ['./', 'assets/'];

export function authInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const router = inject(Router);
  const deviceDetector = inject(DeviceDetectorService);
  const device = deviceDetector.userAgent();

  const skipAuth = request.headers.get('skipAuth');
  const skipAuthRefresh = request.headers.get('skipAuthRefresh');

  request = request.clone({
    headers: request.headers.delete('skipAuth').delete('skipAuthRefresh')
  });

  if (allowWithoutToken.some((item) => request.url.includes(item)) || skipAuth === '1') {
    return next(request);
  }

  // skipAuthRefresh bypasses the gate — used by follow-up calls inside the refresh
  // flow itself (e.g. getProfile right after token refresh). Without this bypass
  // those calls would deadlock: they'd wait at the closed gate, but the gate only
  // opens after refresh completes, and refresh waits for them.
  if (skipAuthRefresh === '1') {
    return next(addAuthHeader(request, authService, device));
  }

  // Wait at the gate. While a refresh is running, gateOpen$ is `false` and every
  // request — new or retried — sits here until the refresh resolves.
  return gateOpen$.pipe(
    filter((open) => open),
    take(1),
    takeUntil(abort$),
    switchMap(() => {
      // Proactive: if the local token is already expired, refresh first.
      if (authService.authToken$?.value?.isAccessTokenExpired) {
        return triggerRefresh(authService, router).pipe(
          switchMap(() => next(addAuthHeader(request, authService, device)))
        );
      }

      const authedRequest = addAuthHeader(request, authService, device);
      return next(authedRequest).pipe(
        catchError((err) => {
          if (err.status === 401) {
            return triggerRefresh(authService, router).pipe(
              switchMap(() => next(addAuthHeader(request, authService, device)))
            );
          }
          return throwError(() => err);
        })
      );
    })
  );
}

function addAuthHeader(request: HttpRequest<unknown>, authService: AuthService, device: string): HttpRequest<unknown> {
  if (!request.headers.has('Authorization') && authService.authToken$?.value) {
    return request.clone({
      setHeaders: {
        'x-device': device,
        authorization: `bearer ${authService.authToken$.value.accessToken}`
      }
    });
  }
  return request;
}

function triggerRefresh(authService: AuthService, router: Router): Observable<unknown> {
  // If a refresh is already running, share it — don't start another one.
  if (refreshInFlight) {
    return refreshInFlight;
  }

  // Close the gate before any HTTP call goes out, so new requests wait.
  gateOpen$.next(false);

  refreshInFlight = authService.refreshToken(true, true, true).pipe(
    tap(() => {
      // success: reopen the gate so queued requests can proceed
      gateOpen$.next(true);
    }),
    catchError(() => {
      // failure: signal waiting requests to cancel, then redirect to login
      abort$.next();
      from(router.navigate(['/', 'user', 'login']))
        .pipe(switchMap(() => authService.clearData()))
        .subscribe(() => {
          // reopen the gate after cleanup so future logins aren't blocked
          gateOpen$.next(true);
        });
      return EMPTY;
    }),
    finalize(() => {
      refreshInFlight = null;
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  return refreshInFlight;
}